import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import axios from '../../../lib/axios';
import { useToast } from '../../components/ToastProvider';
import { Company, Companies } from './Companies';
import { PageLink, PageTitle } from '../../../_metronic/layout/core';
import { useIntl, FormattedMessage } from 'react-intl';



const AdminAllCompanies = () => {
  const intl = useIntl();
  const { showError } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);

  const navigate = useNavigate();

  const titlePage = intl.formatMessage({ id: 'LABEL.COMPANY_ADMIN_ALL_COMPANIES' });
  const companyBreadCrumbs: Array<PageLink> = [
    {
      title: titlePage, // Translation Key
      path: '/companies/admin-all-companies',
      isSeparator: false,
      isActive: false,
    },

  ];
  const getCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const response = await axios.get<Companies>('/api/companies', {
        params: { search }
      });
      setCompanies(response.data.companies);
      setFilteredCompanies(response.data.companies);
    } catch (error) {
      showError(intl.formatMessage({ id: 'ERROR.FAILED_FETCH_COMPANIES', defaultMessage: 'Failed to fetch companies' }));
      // You might need to add "ERROR.FAILED_FETCH_COMPANIES": "Failed to fetch companies" to your en.json
    } finally {
      setLoadingCompanies(false);
    }
  }, [showError, search, intl]);

  const toggleRow = (id: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleViewCompany = (companyId: number) => {
    if (companyId) {
      navigate(`/companies/admin-view-company`, {
        state: { companyId: companyId }
      });
    } else {
      showError(intl.formatMessage({ id: 'ERROR.COMPANY_NOT_FOUND', defaultMessage: 'Company not found' }));
      // You might need to add "ERROR.COMPANY_NOT_FOUND": "Company not found" to your en.json
    }
  };

  useEffect(() => {
    getCompanies();
  }, [getCompanies]);

  const handleEditClick = (id: number) => {
    navigate(`/companies/edit/${id}`);
  };

  const handleRefresh = () => {
    getCompanies();
  };

  const handleSearchChange = (value: any) => {
    setSearch(value?.value || '');
    getCompanies();
  };

  const searchOptions = companies && companies.length > 0 ? companies.map(company => ({
    value: company.name,
    label: company.name
  })) : [];

  const TableHeaderCompany = () => (
    <thead>
      <tr className="fw-bold fs-6 text-gray-800 border-bottom border-gray-200">
        <th><FormattedMessage id="GENERAL.ID" defaultMessage="ID" /></th>
        <th></th>
        <th><FormattedMessage id="GENERAL.NAME" defaultMessage="Name" /></th>
        <th><FormattedMessage id="GENERAL.ADDRESS" defaultMessage="Address" /></th>
        <th><FormattedMessage id="GENERAL.EMAIL" defaultMessage="Email" /></th>
        <th><FormattedMessage id="GENERAL.PHONE" defaultMessage="Phone" /></th>
        <th><FormattedMessage id="GENERAL.WEBSITE" defaultMessage="Website" /></th>
        <th><FormattedMessage id="GENERAL.ACTIONS" defaultMessage="Actions" /></th>
      </tr>
    </thead>
  );

  const TableBodyCompany = () => (
    <tbody>
      {filteredCompanies && filteredCompanies.length > 0 ? (
        filteredCompanies.map((company: Company) => (
          <React.Fragment key={company.id}>
            <tr>
              <td>{company.id}</td>
              <td>
                <Button className="btn btn-sm btn-success" onClick={() => toggleRow(company.id)}>
                  <i className="fa-solid fa-circle-info"></i>
                </Button>
              </td>
              <td>{company.name}</td>
              <td>{company.address}</td>
              <td>{company.email}</td>
              <td>{company.phone}</td>
              <td>{company.website}</td>
              <td>
                <Button variant="info" onClick={() => handleEditClick(company.id)}>
                  <FormattedMessage id="GENERAL.EDIT" defaultMessage="Edit" />
                </Button>
              </td>
            </tr>
            {expandedRows.has(company.id) && (
              <tr>
                <td colSpan={8}>
                  <div>
                    <strong><FormattedMessage id="GENERAL.ID" defaultMessage="ID" />:</strong> {company.id}<br />
                    <strong><FormattedMessage id="GENERAL.NAME" defaultMessage="Name" />:</strong> {company.name}<br />
                    <strong><FormattedMessage id="GENERAL.ADDRESS" defaultMessage="Address" />:</strong> {company.address}<br />
                    <strong><FormattedMessage id="GENERAL.EMAIL" defaultMessage="Email" />:</strong> {company.email}<br />
                    <strong><FormattedMessage id="GENERAL.PHONE" defaultMessage="Phone" />:</strong> {company.phone}<br />
                    <strong><FormattedMessage id="GENERAL.WEBSITE" defaultMessage="Website" />:</strong> {company.website}<br />
                    <strong><FormattedMessage id="GENERAL.HAS_MULTIPLE_ACTIVE_SUBSCRIPTION" defaultMessage="Has Multiple Active Subscription" />:</strong> <FormattedMessage id={company.has_multiple ? 'GENERAL.YES' : 'GENERAL.NO'} defaultMessage={company.has_multiple ? 'Yes' : 'No'} />
                  </div>
                  <div className='card'>
                    <div className='card-body'>
                      <Button className='btn btn-md btn-system' onClick={() => handleViewCompany(company.id)}>
                        <i className='fa fa-eye'></i><FormattedMessage id="GENERAL.VIEW_COMPANY" defaultMessage="View Company" />
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))
      ) : (
        <tr>
          <td colSpan={8}>
            <FormattedMessage id="GENERAL.NO_RECORDS" defaultMessage="No companies found" />
          </td>
        </tr>
      )}
    </tbody>
  );

  return (
    <>
      <PageTitle breadcrumbs={companyBreadCrumbs}>
        {titlePage}
      </PageTitle>
      <div className="card card-flush h-xl-100">
        <div className="card-header pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold text-gray-900">
              <FormattedMessage id="COMPANY_ADMIN_ALL_COMPANIES" defaultMessage="All Companies" />
            </span>
            <span className="text-gray-500 mt-1 fw-semibold fs-6">
              <FormattedMessage id="COMPANY_ADMIN_ALL_COMPANIES" defaultMessage="List of all companies" />
              {/* You might need to add "COMPANY.LIST_OF_ALL_COMPANIES": "List of all companies" to your en.json */}
            </span>
          </h3>
          <div className="card-toolbar">
            <div className='flex flex-row-fluid'>
              <a href="/companies/admin-create-company" className="btn btn-sm btn-light-primary">
                <i className="fa fa-plus"></i>
                <FormattedMessage id="COMPANY.CREATE_NEW_COMPANY" defaultMessage="Create New Company" />
                {/* You might need to add "COMPANY.CREATE_NEW_COMPANY": "Create New Company" to your en.json */}
              </a>
              <Button className="btn btn-sm btn-light-system" onClick={handleRefresh}>
                <i className="fa fa-refresh"></i>
                <FormattedMessage id="GENERAL.REFRESH" defaultMessage="Refresh" />
                {/* You might need to add "GENERAL.REFRESH": "Refresh" to your en.json */}
              </Button>
              <div className="d-flex align-items-center ms-auto">
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable
                  isSearchable
                  name="companySearch"
                  options={searchOptions}
                  onChange={handleSearchChange}
                  placeholder={intl.formatMessage({ id: 'GENERAL.SEARCH_COMPANY', defaultMessage: 'Search company...' })}
                // You might need to add "GENERAL.SEARCH_COMPANY": "Search company..." to your en.json
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          {loadingCompanies ? (
            <>
              <Spinner animation="border" />
            </>
          ) : (
            <div className="container" id="kt_docs_content_container">
              <div className="card mb-2">
                <div className="card-body fs-6 py-15 px-10 py-lg-15 px-lg-15">
                  <div className="table-responsive">
                    <table className="table table-striped gy-7 gs-7">
                      <TableHeaderCompany />
                      <TableBodyCompany />
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminAllCompanies;
